import { Field } from '../../components/Modal.jsx';
import { localeFor } from '../../lib/format.js';
import { useT } from '../../lib/i18n.jsx';

// The domain holder, recorded at the registry. The API requires address_1,
// postal_code, city, country (ISO2) and phone (the registrar's own rules) and
// answers 422 per field; the same fields are checked here first so a typo does
// not cost one of the few order attempts per minute.
export const REQUIRED = ['address_1', 'postal_code', 'city', 'country', 'phone'];
const STORAGE_KEY = 'domainRegistrant';
const COUNTRIES = ['NL', 'BE', 'DE', 'FR', 'LU', 'AT', 'CH', 'GB', 'IE', 'ES', 'PT', 'IT', 'DK', 'SE', 'NO', 'FI', 'PL', 'CZ', 'US'];
const EMPTY = { name: '', company: '', email: '', address_1: '', postal_code: '', city: '', country: 'NL', phone: '' };

export function initialRegistrant(user) {
  let saved = {};
  try {
    saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch {
    saved = {};
  }
  const out = { ...EMPTY };
  for (const k of Object.keys(EMPTY)) if (typeof saved[k] === 'string') out[k] = saved[k];
  if (!out.name && user?.full_name) out.name = user.full_name;
  if (!out.email && user?.email) out.email = user.email;
  return out;
}

export function rememberRegistrant(registrant) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(registrant));
  } catch {
    /* storage full or blocked: the form simply starts empty next time */
  }
}

// { field: 'required' } for empty required fields.
export function missingFields(registrant) {
  const out = {};
  for (const k of REQUIRED) if (!String(registrant[k] || '').trim()) out[`registrant.${k}`] = 'required';
  return out;
}

// FastAPI-style 422 detail → { 'registrant.phone': 'required' | 'invalid', auth_code: …, … }
export function fieldErrorsFrom422(detail) {
  const out = {};
  if (!Array.isArray(detail)) return out;
  for (const d of detail) {
    const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body') : [];
    if (!loc.length) continue;
    out[loc.join('.')] = d.msg === 'required' || d.type === 'missing' ? 'required' : 'invalid';
  }
  return out;
}

export function errorText(code, t) {
  if (!code) return '';
  return code === 'required' ? t('order.err.required') : t('order.err.invalid');
}

export default function RegistrantFields({ value, onChange, errors = {}, lang, disabled }) {
  const t = useT();
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const err = (k) => errorText(errors[`registrant.${k}`], t);
  let regionName = (code) => code;
  try {
    const names = new Intl.DisplayNames([localeFor(lang)], { type: 'region' });
    regionName = (code) => names.of(code) || code;
  } catch {
    /* older browsers: show the ISO code */
  }
  const countries = COUNTRIES.includes(value.country) || !value.country ? COUNTRIES : [value.country, ...COUNTRIES];

  return (
    <fieldset className="registrant" disabled={disabled}>
      <legend>{t('registrant.title')}</legend>
      <p className="field-hint" style={{ margin: '0 0 4px' }}>{t('registrant.hint')}</p>
      <div className="field-row">
        <Field label={t('registrant.name')} error={err('name')}>
          <input autoComplete="name" maxLength={255} value={value.name} onChange={set('name')} />
        </Field>
        <Field label={t('registrant.company')} error={err('company_name') || err('company')}>
          <input autoComplete="organization" maxLength={255} value={value.company} onChange={set('company')} />
        </Field>
      </div>
      <Field label={t('registrant.address')} error={err('address_1')}>
        <input autoComplete="street-address" maxLength={255} value={value.address_1} onChange={set('address_1')} />
      </Field>
      <div className="field-row">
        <Field label={t('registrant.postalCode')} error={err('postal_code')}>
          <input autoComplete="postal-code" maxLength={32} value={value.postal_code} onChange={set('postal_code')} />
        </Field>
        <Field label={t('registrant.city')} error={err('city')}>
          <input autoComplete="address-level2" maxLength={255} value={value.city} onChange={set('city')} />
        </Field>
      </div>
      <Field label={t('registrant.country')} error={err('country')}>
        <select autoComplete="country" value={value.country} onChange={set('country')}>
          {countries.map((c) => (
            <option key={c} value={c}>
              {regionName(c)}
            </option>
          ))}
        </select>
      </Field>
      <div className="field-row">
        <Field label={t('registrant.phone')} hint={err('phone') ? undefined : t('registrant.phone.hint')} error={err('phone')}>
          <input type="tel" autoComplete="tel" maxLength={25} value={value.phone} onChange={set('phone')} />
        </Field>
        <Field label={t('registrant.email')} error={err('email')}>
          <input type="email" autoComplete="email" maxLength={255} value={value.email} onChange={set('email')} />
        </Field>
      </div>
    </fieldset>
  );
}
